import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Navigation } from '../components/Navigation';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import saldobalanceRaw from './saldobalance_data.json';

const formatDKK = (val) => {
  if (val >= 1000000) return `${(val / 1000000).toFixed(2)}M`;
  if (val >= 1000) return `${(val / 1000).toFixed(0)}K`;
  return val.toLocaleString('da-DK');
};

const formatTotal = (val) => val.toLocaleString('da-DK', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const periodOptions = [
  { value: 'last_3', label: 'Sidste 3 måneder' },
  { value: 'last_6', label: 'Sidste 6 måneder' },
  { value: 'last_12', label: 'Sidste 12 måneder' },
  { value: 'this_quarter', label: 'Dette kvartal' },
  { value: 'this_half', label: 'Dette halvår' },
  { value: 'this_year', label: 'Dette år' },
];

const parseMonth = (label) => {
  const monthMap = {
    'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'may': 4, 'maj': 4, 'jun': 5,
    'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'okt': 9, 'nov': 10, 'dec': 11
  };
  const parts = label.split('-');
  if (parts.length !== 2) return null;
  const month = monthMap[parts[0].toLowerCase()];
  if (month === undefined) return null;
  return new Date(2000 + parseInt(parts[1]), month, 1);
};

const filterByPeriod = (data, period) => {
  const now = new Date();
  let startDate;
  switch (period) {
    case 'last_3':
      startDate = new Date(now.getFullYear(), now.getMonth() - 3, 1); break;
    case 'last_6':
      startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1); break;
    case 'last_12':
      startDate = new Date(now.getFullYear(), now.getMonth() - 12, 1); break;
    case 'this_quarter': {
      const q = Math.floor(now.getMonth() / 3);
      startDate = new Date(now.getFullYear(), q * 3, 1); break;
    }
    case 'this_half': {
      const h = Math.floor(now.getMonth() / 6);
      startDate = new Date(now.getFullYear(), h * 6, 1); break;
    }
    case 'this_year':
      startDate = new Date(now.getFullYear(), 0, 1); break;
    default:
      startDate = new Date(now.getFullYear(), now.getMonth() - 6, 1);
  }
  return data.filter((d) => {
    const md = parseMonth(d.month);
    return md && md >= startDate && md <= now;
  });
};

const chartStyle = {
  grid: "rgba(255,255,255,0.05)",
  tick: { fill: 'rgba(255,255,255,0.5)', fontSize: 12 },
  tickRight: { fill: 'rgba(255,255,255,0.3)', fontSize: 12 },
  tooltip: {
    contentStyle: { background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 },
    labelStyle: { color: 'rgba(255,255,255,0.6)' },
    itemStyle: { color: '#ffffff' },
    cursor: { fill: 'rgba(255,255,255,0.05)' },
  },
};

const pctFormatter = (v, name) => [name.includes('%') ? `${v.toFixed(2)}%` : `${v.toLocaleString('da-DK')} kr`];
const pct1Formatter = (v, name) => [name.includes('%') ? `${v.toFixed(1)}%` : `${v.toLocaleString('da-DK')} kr`];

const revenueOptions = [
  { value: 'omsaetning', label: 'Samlet omsætning', color: '#1E6F7A' },
  { value: 'koerende_salg', label: 'Kørende salg', color: '#2E7D32' },
  { value: 'online_salg', label: 'Online salg', color: '#1565C0' },
];

const costOptions = [
  { value: 'samlede_omkostninger', label: 'Samlede omkostninger', color: '#B3261E', pctKey: 'samlede_pct' },
  { value: 'vareforbrug', label: 'Vareforbrug', color: '#C77700', pctKey: 'vareforbrug_pct' },
  { value: 'fragt', label: 'Fragtomkostninger', color: '#7B1FA2', pctKey: 'fragt_pct' },
];

const RevenueChart = ({ data, loading }) => {
  const [selected, setSelected] = useState('omsaetning');
  const opt = revenueOptions.find((o) => o.value === selected);
  const total = data.reduce((s, d) => s + (d[selected] || 0), 0);
  return (
    <div className="bg-[#151515] rounded-xl p-5 border border-white/5" data-testid="omsaetning-chart">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-white/80">Omsætning pr. måned</h2>
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
          className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-white/30"
          data-testid="revenue-type-select">
          {revenueOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {loading ? <div className="h-64 flex items-center justify-center text-white/40">Indlæser...</div> : (
        <ResponsiveContainer key={selected} width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartStyle.grid} />
            <XAxis dataKey="month" tick={chartStyle.tick} />
            <YAxis tick={chartStyle.tick} tickFormatter={formatDKK} />
            <Tooltip {...chartStyle.tooltip} formatter={(v) => [`${v.toLocaleString('da-DK')} kr`]} />
            <Bar dataKey={selected} name={opt.label} fill={opt.color} radius={[4, 4, 0, 0]} fillOpacity={0.85} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      {!loading && <div className="mt-3 pt-3 border-t border-white/5 flex justify-between items-center" data-testid="revenue-total">
        <span className="text-xs text-white/40">Samlet for perioden</span>
        <span className="text-sm font-semibold" style={{ color: opt.color }}>{formatTotal(total)} kr</span>
      </div>}
    </div>
  );
};

const CostChart = ({ data, loading }) => {
  const [selected, setSelected] = useState('samlede_omkostninger');
  const opt = costOptions.find((o) => o.value === selected);
  const total = data.reduce((s, d) => s + (d[selected] || 0), 0);
  const totalOms = data.reduce((s, d) => s + (d.omsaetning || 0), 0);
  const avgPct = totalOms > 0 ? ((total / totalOms) * 100).toFixed(1) : '0';
  return (
    <div className="bg-[#151515] rounded-xl p-5 border border-white/5" data-testid="cost-chart">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-medium text-white/80">Omkostninger pr. måned</h2>
        <select value={selected} onChange={(e) => setSelected(e.target.value)}
          className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-white/30"
          data-testid="cost-type-select">
          {costOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {loading ? <div className="h-64 flex items-center justify-center text-white/40">Indlæser...</div> : (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartStyle.grid} />
            <XAxis dataKey="month" tick={chartStyle.tick} />
            <YAxis yAxisId="left" tick={chartStyle.tick} tickFormatter={formatDKK} />
            <YAxis yAxisId="right" orientation="right" tick={chartStyle.tickRight} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
            <Tooltip {...chartStyle.tooltip} formatter={(v, name) => [name.includes('%') ? `${v.toFixed(1)}%` : `${v.toLocaleString('da-DK')} kr`]} />
            <Bar yAxisId="left" dataKey={selected} name={opt.label} fill={opt.color} radius={[4, 4, 0, 0]} fillOpacity={0.85} />
            <Line yAxisId="right" type="monotone" dataKey={opt.pctKey} name="% af omsætning" stroke="#ffffff" strokeWidth={2} dot={{ fill: '#ffffff', r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <p className="text-xs text-white/30 mt-2"><span className="inline-block w-3 h-0.5 bg-white align-middle mr-1"></span> % af omsætning</p>
      {!loading && <div className="mt-2 pt-3 border-t border-white/5 flex justify-between items-center" data-testid="cost-total">
        <span className="text-xs text-white/40">Samlet for perioden</span>
        <span className="text-sm font-semibold" style={{ color: opt.color }}>{formatTotal(total)} kr <span className="text-xs text-white/40 ml-1">({avgPct}% af oms.)</span></span>
      </div>}
    </div>
  );
};

const DBChart = ({ data, loading }) => {
  const totalDB = data.reduce((s, d) => s + (d.daekningsbidrag || 0), 0);
  const totalOms = data.reduce((s, d) => s + (d.omsaetning || 0), 0);
  const dbPct = totalOms > 0 ? ((totalDB / totalOms) * 100).toFixed(1) : '0';
  return (
    <div className="bg-[#151515] rounded-xl p-5 border border-white/5" data-testid="db-chart">
      <h2 className="text-sm font-medium text-white/80 mb-4">Dækningsbidrag pr. måned</h2>
      {loading ? <div className="h-64 flex items-center justify-center text-white/40">Indlæser...</div> : (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartStyle.grid} />
            <XAxis dataKey="month" tick={chartStyle.tick} />
            <YAxis yAxisId="left" tick={chartStyle.tick} tickFormatter={formatDKK} />
            <YAxis yAxisId="right" orientation="right" tick={chartStyle.tickRight} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
            <Tooltip {...chartStyle.tooltip} formatter={(v, name) => [name.includes('%') ? `${v.toFixed(1)}%` : `${v.toLocaleString('da-DK')} kr`]} />
            <Bar yAxisId="left" dataKey="daekningsbidrag" name="Dækningsbidrag" fill="#2E7D32" radius={[4, 4, 0, 0]} fillOpacity={0.85} />
            <Line yAxisId="left" type="monotone" dataKey="breakeven" name="Break even" stroke="#EF5350" strokeWidth={2} strokeDasharray="6 3" dot={{ fill: '#EF5350', r: 3 }} />
            <Line yAxisId="right" type="monotone" dataKey="db_pct" name="DB% af omsætning" stroke="#ffffff" strokeWidth={2} dot={{ fill: '#ffffff', r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <div className="mt-2 space-y-0.5">
        <p className="text-xs text-white/30"><span className="inline-block w-3 h-0.5 bg-white align-middle mr-1"></span> % af omsætning</p>
        <p className="text-xs text-white/30"><span className="inline-block w-3 h-0.5 align-middle mr-1" style={{ background: '#EF5350', borderTop: '1px dashed #EF5350' }}></span> Break even (sum af udgifter)</p>
      </div>
      {!loading && <div className="mt-2 pt-3 border-t border-white/5 flex justify-between items-center" data-testid="db-total">
        <span className="text-xs text-white/40">Samlet for perioden</span>
        <span className="text-sm font-semibold text-[#2E7D32]">{formatTotal(totalDB)} kr <span className="text-xs text-white/40 ml-1">({dbPct}% af oms.)</span></span>
      </div>}
    </div>
  );
};

const SimpleChart = ({ data, loading, title, dataKey, pctKey, color, pctDomain, testId }) => {
  const total = data.reduce((s, d) => s + (d[dataKey] || 0), 0);
  const totalDB = data.reduce((s, d) => s + (d.daekningsbidrag || 0), 0);
  const pctOfDB = totalDB > 0 ? ((total / totalDB) * 100).toFixed(1) : '0';
  return (
    <div className="bg-[#151515] rounded-xl p-5 border border-white/5" data-testid={testId}>
      <h2 className="text-sm font-medium text-white/80 mb-4">{title}</h2>
      {loading ? <div className="h-64 flex items-center justify-center text-white/40">Indlæser...</div> : (
        <ResponsiveContainer width="100%" height={280}>
          <ComposedChart data={data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={chartStyle.grid} />
            <XAxis dataKey="month" tick={chartStyle.tick} />
            <YAxis yAxisId="left" tick={chartStyle.tick} tickFormatter={formatDKK} />
            <YAxis yAxisId="right" orientation="right" tick={chartStyle.tickRight} tickFormatter={(v) => `${v}%`} domain={pctDomain || [0, 'auto']} />
            <Tooltip {...chartStyle.tooltip} formatter={pctFormatter} />
            <Bar yAxisId="left" dataKey={dataKey} name={title.replace(' pr. måned', '')} fill={color} radius={[4, 4, 0, 0]} fillOpacity={0.85} />
            <Line yAxisId="right" type="monotone" dataKey={pctKey} name="% af DB" stroke="#ffffff" strokeWidth={2} dot={{ fill: '#ffffff', r: 3 }} />
          </ComposedChart>
        </ResponsiveContainer>
      )}
      <p className="text-xs text-white/30 mt-2"><span className="inline-block w-3 h-0.5 bg-white align-middle mr-1"></span> % af DB</p>
      {!loading && <div className="mt-2 pt-3 border-t border-white/5 flex justify-between items-center" data-testid={`${testId}-total`}>
        <span className="text-xs text-white/40">Samlet for perioden</span>
        <span className="text-sm font-semibold" style={{ color }}>{formatTotal(total)} kr <span className="text-xs text-white/40 ml-1">({pctOfDB}% af DB)</span></span>
      </div>}
    </div>
  );
};

// --- RAPPORT TAB: Saldobalance fra Google Sheet (statisk embedded data) ---
const isNumericCell = (cell) => {
  if (!cell || typeof cell !== 'string') return false;
  return /^-?[\d.,]+$/.test(cell.trim());
};

const parseNumericCell = (cell) => {
  if (!cell) return 0;
  const s = cell.toString().replace(/\./g, '').replace(',', '.');
  const n = parseFloat(s);
  return isNaN(n) ? 0 : n;
};

const formatNumericCell = (cell) => {
  if (!cell || cell === '0,00' || cell === '0.00') return <span className="text-white/20">—</span>;
  const n = parseNumericCell(cell);
  const isNegative = n < 0;
  return <span className={isNegative ? 'text-emerald-400' : 'text-white/80'}>{cell}</span>;
};

const isSectionHeader = (row) => {
  const konto = (row[0] || '').trim();
  const navn = (row[1] || '').trim();
  if (konto) return false;
  if (!navn) return false;
  const hasNumbers = row.slice(2).some((c) => c && c.trim() && c.trim() !== '0,00');
  return !hasNumbers;
};

const isTotalRow = (row) => {
  const navn = (row[1] || '').toLowerCase();
  return (
    navn.includes('i alt') ||
    navn.includes('ialt') ||
    navn.includes('resultat') ||
    navn.includes('bruttoavance')
  );
};

const isGrandTotal = (row) => {
  const navn = (row[1] || '').toUpperCase();
  return navn === 'RESULTAT EFTER SKAT';
};

const SaldobalanceRapport = () => {
  const headerRow = saldobalanceRaw[0] || [];
  const months = headerRow.slice(2);
  const dataRows = saldobalanceRaw.slice(1).filter((r) => r && r.length > 0);

  const [yearFilter, setYearFilter] = useState('alle');
  const [search, setSearch] = useState('');

  const visibleColIndices = months.map((m, i) => {
    if (yearFilter === 'alle') return i;
    if (yearFilter === '2025' && m.endsWith('-25')) return i;
    if (yearFilter === '2026' && m.endsWith('-26')) return i;
    return null;
  }).filter((i) => i !== null);

  const filteredRows = dataRows.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (r[0] || '').toLowerCase().includes(q) || (r[1] || '').toLowerCase().includes(q);
  });

  return (
    <div className="bg-[#151515] rounded-xl border border-white/5 overflow-hidden" data-testid="saldobalance-rapport">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-white/5 flex-wrap">
        <div>
          <h2 className="text-sm font-medium text-white/90">Saldobalance — Rapport</h2>
          <p className="text-[10px] text-white/40 mt-0.5">Data: Saldobalance 2026 (statisk indlæst)</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <input
            data-testid="rapport-search"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Søg konto eller navn..."
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70 placeholder-white/30 w-48"
          />
          <select
            data-testid="rapport-year-filter"
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="bg-white/5 border border-white/10 rounded px-2 py-1 text-white/70"
          >
            <option value="alle">Alle år</option>
            <option value="2025">2025</option>
            <option value="2026">2026</option>
          </select>
        </div>
      </div>

      <div className="overflow-x-auto max-h-[80vh]">
        <table className="w-full text-xs" data-testid="rapport-table">
          <thead className="sticky top-0 bg-[#1a1a1a] z-10">
            <tr className="border-b border-white/10">
              <th className="text-left py-2 pl-4 pr-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold sticky left-0 bg-[#1a1a1a] w-20">Konto</th>
              <th className="text-left py-2 pr-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold sticky bg-[#1a1a1a] min-w-[240px]" style={{ left: '5rem' }}>Navn</th>
              {visibleColIndices.map((ci) => (
                <th key={ci} className="text-right py-2 px-2 text-[10px] uppercase tracking-widest text-white/40 font-semibold whitespace-nowrap">
                  {months[ci]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row, idx) => {
              const sectionHeader = isSectionHeader(row);
              const totalRow = isTotalRow(row);
              const grand = isGrandTotal(row);

              if (sectionHeader) {
                return (
                  <tr key={idx} className="bg-[#1a1a1a]">
                    <td colSpan={2 + visibleColIndices.length} className="py-2 pl-4 pr-2 text-[11px] uppercase tracking-widest text-[#1E6F7A] font-semibold">
                      {row[1]}
                    </td>
                  </tr>
                );
              }

              const rowBg = grand ? 'bg-emerald-500/10' : totalRow ? 'bg-white/[0.03]' : '';
              const stickyBg = grand ? 'bg-emerald-500/10' : totalRow ? 'bg-[#1a1a1a]' : 'bg-[#151515]';

              return (
                <tr
                  key={idx}
                  className={`border-b border-white/[0.03] ${rowBg} ${grand || totalRow ? 'font-medium' : 'hover:bg-white/[0.015]'}`}
                >
                  <td className={`py-1.5 pl-4 pr-2 tabular-nums sticky left-0 ${stickyBg} ${grand ? 'text-emerald-300' : totalRow ? 'text-white/80' : 'text-white/50'}`}>
                    {row[0] || ''}
                  </td>
                  <td className={`py-1.5 pr-2 sticky ${stickyBg} ${grand ? 'text-emerald-200' : totalRow ? 'text-white/90' : 'text-white/70'}`} style={{ left: '5rem' }}>
                    {row[1] || ''}
                  </td>
                  {visibleColIndices.map((ci) => {
                    const cell = row[ci + 2];
                    return (
                      <td key={ci} className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">
                        {isNumericCell(cell) ? formatNumericCell(cell) : <span className="text-white/20">{cell || '—'}</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="p-3 border-t border-white/5 text-[10px] text-white/30 flex items-center justify-between">
        <span>{filteredRows.length} rækker vist</span>
        <span>Grøn tekst i tal = indtægt/kredit (negativt fortegn)</span>
      </div>
    </div>
  );
};



const Saldobalance = () => {
  const [activeTab, setActiveTab] = useState('grafer');
  const [allData, setAllData] = useState([]);
  const [period, setPeriod] = useState('last_12');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/saldobalance/omsaetning');
        const raw = res.data.data || [];
        const enriched = raw.map((d) => ({
          ...d,
          samlede_pct: d.omsaetning > 0 ? (d.samlede_omkostninger / d.omsaetning) * 100 : 0,
          vareforbrug_pct: d.omsaetning > 0 ? (d.vareforbrug / d.omsaetning) * 100 : 0,
          fragt_pct: d.omsaetning > 0 ? (d.fragt / d.omsaetning) * 100 : 0,
          db_pct: d.omsaetning > 0 ? (d.daekningsbidrag / d.omsaetning) * 100 : 0,
          loen_pct: d.daekningsbidrag > 0 ? (d.loenninger / d.daekningsbidrag) * 100 : 0,
          farveproever_pct: d.daekningsbidrag > 0 ? (d.farveproever / d.daekningsbidrag) * 100 : 0,
          rejse_moede_pct: d.daekningsbidrag > 0 ? (d.rejse_moede / d.daekningsbidrag) * 100 : 0,
          salgsomk_pct: d.daekningsbidrag > 0 ? (d.salgsomkostninger / d.daekningsbidrag) * 100 : 0,
          autodrift_pct: d.daekningsbidrag > 0 ? (d.autodrift / d.daekningsbidrag) * 100 : 0,
          lokaleomk_pct: d.daekningsbidrag > 0 ? (d.lokaleomkostninger / d.daekningsbidrag) * 100 : 0,
          software_pct: d.daekningsbidrag > 0 ? (d.software / d.daekningsbidrag) * 100 : 0,
          admin_omk_pct: d.daekningsbidrag > 0 ? (d.admin_omk / d.daekningsbidrag) * 100 : 0,
          resultat_pct: d.daekningsbidrag > 0 ? (d.resultat_foer_skat / d.daekningsbidrag) * 100 : 0,
          breakeven: (() => {
            const now = new Date();
            const parts = d.month.split('-');
            const monthMap = { jan:0,feb:1,mar:2,apr:3,may:4,maj:4,jun:5,jul:6,aug:7,sep:8,oct:9,okt:9,nov:10,dec:11 };
            const m = monthMap[parts[0].toLowerCase()];
            const y = 2000 + parseInt(parts[1]);
            if (y === now.getFullYear() && m === now.getMonth()) return null;
            return (d.loenninger || 0) + (d.farveproever || 0) + (d.rejse_moede || 0) + (d.salgsomkostninger || 0) + (d.autodrift || 0) + (d.lokaleomkostninger || 0) + (d.software || 0) + (d.admin_omk || 0);
          })(),
        }));
        setAllData(enriched);
      } catch (e) {
        console.error('Error fetching saldobalance:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredData = filterByPeriod(allData, period);

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white p-6" data-testid="saldobalance-page">
      <Navigation />
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <h1 className="text-2xl font-bold">Saldobalance</h1>
        {activeTab === 'grafer' && (
          <select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="bg-[#1A1A1A] text-white border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-white/30"
            data-testid="saldobalance-period-select"
          >
            {periodOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 mb-6 border-b border-white/10" data-testid="saldobalance-tabs">
        <button
          data-testid="tab-grafer"
          onClick={() => setActiveTab('grafer')}
          className={`px-4 py-2 text-sm transition-colors border-b-2 ${activeTab === 'grafer' ? 'border-[#1E6F7A] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
        >
          Grafer
        </button>
        <button
          data-testid="tab-rapport"
          onClick={() => setActiveTab('rapport')}
          className={`px-4 py-2 text-sm transition-colors border-b-2 ${activeTab === 'rapport' ? 'border-[#1E6F7A] text-white' : 'border-transparent text-white/50 hover:text-white/80'}`}
        >
          Rapport
        </button>
      </div>

      {activeTab === 'grafer' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" key={period}>
          <RevenueChart data={filteredData} loading={loading} />
          <CostChart data={filteredData} loading={loading} />
          <DBChart data={filteredData} loading={loading} />
          <SimpleChart data={filteredData} loading={loading} title="Lønninger pr. måned" dataKey="loenninger" pctKey="loen_pct" color="#0288D1" testId="loen-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Farveprøver pr. måned" dataKey="farveproever" pctKey="farveproever_pct" color="#E91E63" testId="farveproever-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Rejse- og mødeomk. pr. måned" dataKey="rejse_moede" pctKey="rejse_moede_pct" color="#FF6F00" testId="rejse-moede-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Salgsomkostninger pr. måned" dataKey="salgsomkostninger" pctKey="salgsomk_pct" color="#5C6BC0" testId="salgsomk-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Autodrift pr. måned" dataKey="autodrift" pctKey="autodrift_pct" color="#00897B" testId="autodrift-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Lokaleomkostninger pr. måned" dataKey="lokaleomkostninger" pctKey="lokaleomk_pct" color="#8D6E63" testId="lokaleomk-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Software pr. måned" dataKey="software" pctKey="software_pct" color="#7E57C2" testId="software-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Adm. omkostninger pr. måned" dataKey="admin_omk" pctKey="admin_omk_pct" color="#546E7A" testId="admin-omk-chart" />
          <SimpleChart data={filteredData} loading={loading} title="Resultat før skat pr. måned" dataKey="resultat_foer_skat" pctKey="resultat_pct" color="#43A047" pctDomain={['auto', 'auto']} testId="resultat-chart" />
        </div>
      )}

      {activeTab === 'rapport' && <SaldobalanceRapport />}
    </div>
  );
};

export default Saldobalance;
